/**
 * Nav item registry for SidebarNav.
 *
 * All possible nav items are defined here with their metadata.
 * The layout store holds user-controlled `order` and `hidden` arrays.
 * SidebarNav renders from this registry, sorted by the layout store order.
 *
 * Terminology-aware labels (e.g. "Lists") go through `useTerminology()` at
 * render time using the `termKey`. Static labels use `staticLabel`.
 *
 * Groups (like Gather & Share) are represented as a parent item with children.
 * Children are reorderable within the group but not promoted to top-level.
 */

import type { TermKey } from '@sevfy/shared'

export interface NavItemDef {
  /** Unique stable identifier - stored in layoutStore.nav.order */
  id: string
  /** Material icon name */
  icon: string
  /** Router path (undefined for group headers) */
  path?: string
  /** Resolved via useTerminology() at render time */
  termKey?: TermKey
  /** Fixed label (overrides termKey if set) */
  staticLabel?: string
  /** Group this item belongs to (for sub-items) */
  groupId?: string
  /** This item acts as a collapsible group header */
  isGroup?: boolean
  /** IDs of child items (only for group items) */
  childIds?: string[]
  /** Only visible to workspace owners and admins */
  requiresOwnerAdmin?: boolean
  /** User cannot hide this item */
  permanent?: boolean
}

/**
 * A dynamic nav item generated from a user-created DashboardPage.
 * IDs are prefixed with "dp_" (e.g. "dp_abc123").
 */
export interface DashboardPageNavItemDef extends NavItemDef {
  isDashboardPage: true
  pageId: string
}

/** Prefix for dashboard-page nav item IDs */
export const DASHBOARD_PAGE_ID_PREFIX = 'dp_'

/** Build a DashboardPageNavItemDef from a page id, name, and slug */
export function makeDashboardPageNavItem(
  pageId: string,
  name: string,
  slug: string
): DashboardPageNavItemDef {
  return {
    id: `${DASHBOARD_PAGE_ID_PREFIX}${pageId}`,
    icon: 'dashboard_customize',
    path: `/d/${slug}`,
    staticLabel: name,
    isDashboardPage: true,
    pageId,
  }
}

/** Returns true if a nav item ID refers to a dashboard page */
export function isDashboardPageId(id: string): boolean {
  return id.startsWith(DASHBOARD_PAGE_ID_PREFIX)
}

/** Extract the DashboardPage.id from a dp_ nav item id */
export function extractPageId(navItemId: string): string {
  return navItemId.slice(DASHBOARD_PAGE_ID_PREFIX.length)
}

/**
 * All nav item definitions.
 * Order here is the factory default - layout store can override.
 */
export const NAV_ITEM_DEFS: NavItemDef[] = [
  {
    id: 'dashboard',
    icon: 'dashboard',
    path: '/dashboard',
    staticLabel: 'Dashboard',
    permanent: true,
  },
  {
    id: 'lists',
    icon: 'workspaces',
    path: '/lists',
    termKey: 'list',       // plural resolved at render: "Lists"
  },
  {
    id: 'entries',
    icon: 'description',
    path: '/entries',
    termKey: 'entry',      // plural resolved at render: "Entries"
  },
  {
    id: 'flows',
    icon: 'bolt',
    path: '/flows',
    termKey: 'flow',
  },
  {
    id: 'gather',
    icon: 'share',
    staticLabel: 'Gather & Share',
    isGroup: true,
    childIds: ['forms', 'sheets', 'charts'],
  },
  {
    id: 'forms',
    icon: 'assignment',
    path: '/forms',
    termKey: 'form',
    groupId: 'gather',
  },
  {
    id: 'sheets',
    icon: 'table_view',
    path: '/sheets',
    termKey: 'sheet',
    groupId: 'gather',
  },
  {
    id: 'charts',
    icon: 'bar_chart',
    path: '/charts',
    termKey: 'chart',
    groupId: 'gather',
  },
  {
    id: 'notifications',
    icon: 'notifications',
    path: '/notifications',
    staticLabel: 'Notifications',
  },
  {
    id: 'flags',
    icon: 'flag',
    path: '/flags',
    staticLabel: 'Review Queue',
  },
  {
    id: 'help',
    icon: 'help',
    path: '/help',
    staticLabel: 'Help',
  },
]

/** All top-level item IDs (excludes sub-items that live inside groups) */
export const TOP_LEVEL_NAV_IDS = NAV_ITEM_DEFS
  .filter(d => !d.groupId)
  .map(d => d.id)

/** Default top-level ordering */
export const DEFAULT_NAV_ORDER: string[] = TOP_LEVEL_NAV_IDS

export function getNavItemDef(id: string): NavItemDef | undefined {
  return NAV_ITEM_DEFS.find(d => d.id === id)
}
